import { axiosCall } from "@/utils/axiosCall";
import Vue from "vue";
import { selfAssessmentEntity } from "@/components/modules/administration/store/self-assessment-entity";
import { businessCreationActions } from "@/components/modules/administration/store/businessCreationActions";
import {
  deleteAccessControls,
  deleteAssessment,
  deleteBusiness,
  deleteBusinessTags,
  deleteRoles,
  deleteSnapshot,
  deleteTag,
  deleteUsers,
  deleteUsersRoles,
} from "@/components/modules/administration/store/roolbackFunctions";
import {cloneDeep} from "lodash";

const initialState = {
  roles: [],
  users: [],
  shouldSelectTags: false,
  useExistingTags: false,
  tagsToCreate: [],
  selectedTags: [],
  defaultTags: [],
  tags: {selected:[]},
  accountRolesForTag:[],
  createdIds: {
    roles: [],
    roleTemplates: [],
    tags: [],
    defaultTagsAssigned: [],
    newTagsAssigned: [],
    selectedTagsAssigned: [],
    users: [],
    userAccountRoles: [],
  },
  businessCreated: "",
  isSubmitting: false,
};
const state = cloneDeep(
  initialState,
);

const getters = {
  getStoreInfo: (state) => (storeName) => state[storeName],
  getAllStoreInfo: (state) => state,
  getRolesToCreate: (state) => state.roles,
  getShouldSelectTags: (state) => state.shouldSelectTags,
  getUseExistingTags: (state) => state.useExistingTags,
  getTagsToCreate: (state) => state.tagsToCreate,
  getDefaultTags: (state) => state.defaultTags,
  getCreatedIds: (state) => state.createdIds,
  getBusinessCreated: (state) => state.businessCreated,
  getIsSubmitting: (state) => state.isSubmitting,
  getAccountRolesForTag: (state) => state.accountRolesForTag,
};

const actions = {
  setIsSubmitting({ commit }, payload) {
    commit("SET_IS_SUBMITTING", payload);
  },
  setStoreInfo({ commit }, payload) {
    commit("SET_STORE_INFO", payload);
  },
  setCreatedIds({ commit }, payload) {
    commit("SET_CREATED_IDS", payload);
  },
  setShouldSelectTags({ commit }, payload) {
    commit("SET_SHOULD_SELECT_TAGS", payload);
  },
  setUseExistingTags({ commit }, payload) {
    commit("SET_USE_EXISTING_TAGS", payload);
  },
  addTagToCreate({ commit }, { tag, index }) {
    commit("ADD_TAG_TO_CREATE", { tag, index });
  },
  removeTagToCreate({ commit }, index) {
    commit("REMOVE_TAG_TO_CREATE", index);
  },
  setTagIdToRuledBy({ commit }, tag) {
    commit("SET_TAG_ID_TO_RULED_BY", tag);
  },
  setBusinessCreated({ commit }, businessCreated) {
    commit("SET_BUSINESS_CREATED", businessCreated);
  },
  setTagToCreate({ commit }, { field, value, index }) {
    commit("SET_TAG_TO_CREATE", { field, value, index });
  },
  selectTag({ commit }, payload) {
    commit("SELECT_TAG", payload);
  },
  removeFromArray({ commit }, payload) {
    commit("REMOVE_FROM_ARRAY", payload);
  },
  addToArray({ commit }, payload) {
    commit("ADD_TO_ARRAY", payload);
  },
  clearStore({ commit }) {
    commit("CLEAR_STORE");
  },
  clearCreationStatus({ commit }) {
    commit("CLEAR_CREATION_STATUS");
  },
  async submitForm({ state, commit, dispatch }) {
    await dispatch("setIsSubmitting", true);
    await dispatch("createBusiness");
  },
  ...businessCreationActions,
  rollback: async ({ state, dispatch }) => {
    await dispatch("setBusinessCreated", "error");
    await dispatch("setIsSubmitting", false);
    if (
      state.createdIds.accountRoles &&
      state.createdIds.accountRoles.length > 0
    ) {
      await deleteUsersRoles(state.createdIds.accountRoles, dispatch);
    }
    if (state.createdIds.users && state.createdIds.users.length > 0) {
      await deleteUsers(state.createdIds.users, dispatch);
    }

    if (state.createdIds.roles && state.createdIds.roles.length > 0) {
      await deleteAccessControls(state.createdIds.roles, dispatch);
    }

    if (state.createdIds.roles && state.createdIds.roles.length > 0) {
      await deleteRoles(state.createdIds.roles, dispatch);
    }

    if (state.createdIds.assessment) {
      await deleteAssessment(state.createdIds.assessment, dispatch);
    }

    if (state.createdIds.snapshot) {
      await deleteSnapshot(state.createdIds.snapshot, dispatch);
    }

    if (
      state.createdIds.newTagsAssigned &&
      state.createdIds.newTagsAssigned.length > 0
    ) {
      await deleteBusinessTags(
        state.createdIds.newTagsAssigned,
        dispatch,
        "newTagsAssigned"
      );
    }

    if (
      state.createdIds.defaultTagsAssigned &&
      state.createdIds.defaultTagsAssigned.length > 0
    ) {
      await deleteBusinessTags(
        state.createdIds.defaultTagsAssigned,
        dispatch,
        "defaultTagsAssigned"
      );
    }

    if (
      state.createdIds.selectedTagsAssigned &&
      state.createdIds.selectedTagsAssigned.length > 0
    ) {
      await deleteBusinessTags(
        state.createdIds.selectedTagsAssigned,
        dispatch,
        "selectedTagsAssigned"
      );
    }

    if (state.createdIds.tags) {
      await deleteTag(state.createdIds.tags, dispatch);
    }

    if (state.createdIds.business) {
      await deleteBusiness(state.createdIds.business, dispatch);
    }
  },
};

const mutations = {
  REMOVE_FROM_ARRAY: (state, payload) => {
    if (Array.isArray(state[payload.storeName])) {
      state[payload.storeName].splice(payload.index, 1);
    }
  },
  ADD_TO_ARRAY: (state, payload) => {
    if (Array.isArray(state[payload.storeName])) {
      state[payload.storeName].push({});
    }
  },
  ADD_TAG_TO_CREATE: (state, { tag, index }) => {
    if (index === -1) {
      state.tagsToCreate.push(tag);
    } else {
      state.tagsToCreate[index] = tag;
    }
  },
  REMOVE_TAG_TO_CREATE: (state, index) => {
    state.tagsToCreate.splice(index, 1);
  },
  SET_BUSINESS_CREATED: (state, businessCreated) => {
    state.businessCreated = businessCreated;
  },
  SET_TAG_ID_TO_RULED_BY: (state, tag) => {
    if (Array.isArray(state.roleTemplates)) {
      for (let i = 0; i < state.roleTemplates.length; i++) {
        if (state.roleTemplates[i].ruledByValue.name === tag.name) {
          state.roleTemplates[i].ruledByValue.id = tag.id;
        }
      }
    }
  },
  SET_TAG_TO_CREATE: (state, { field, value, index }) => {
    Vue.set(state.tagsToCreate[index], field, value);
  },
  SELECT_TAG: (state, payload) => {
    state.selectedTags.push(payload);
  },
  SET_DEFAULT_TAGS: (state, payload) => {
    state.defaultTags = payload;
  },
  CLEAR_STORE: (state) => {
    Object.keys(state).forEach((key) => {
      delete state[key];
    });
    state = cloneDeep(initialState);
  },
  SET_SHOULD_SELECT_TAGS: (state, payload) => {
    state.shouldSelectTags = payload;
  },
  SET_USE_EXISTING_TAGS: (state, payload) => {
    state.useExistingTags = payload;
  },
  SET_ACCOUNTROLES_FOR_TAG: (state, payload) => {
    state.accountRolesForTag = payload;
  },
  SET_IS_SUBMITTING: (state, payload) => {
    state.isSubmitting = payload;
  },
  CLEAR_CREATION_STATUS: (state) => {
    Object.keys(state.createdIds).forEach((key) => {
      delete state.createdIds[key];
    });
    state.createdIds = cloneDeep(initialState.createdIds );
  },
  SET_STORE_INFO: (state, payload) => {
    Object.keys(payload.data).map((fieldName) => {
      if (payload.storeIndex === -1) {
        if (!state[payload.storeName]) {
          state[payload.storeName] = {};
        }
        state[payload.storeName][fieldName]=payload.data[fieldName]
      } else {
        if (!Array.isArray(state[payload.storeName])) {
          state[payload.storeName] = [];
        }
        if (
          typeof state[payload.storeName][payload.storeIndex] === "undefined"
        ) {
          state[payload.storeName][payload.storeIndex] = {};
        }
        Vue.set(
          state[payload.storeName][payload.storeIndex],
          fieldName,
          payload.data[fieldName]
        );
      }
    });
  },
  SET_CREATED_IDS: (state, payload) => {
    let storeName = "createdIds";
    Object.keys(payload.data).map((fieldName) => {
      if (payload.storeIndex === -1) {
        Vue.set(state[storeName], fieldName, payload.data[fieldName]);
      } else if (typeof payload.storeSecondIndex !== "undefined") {
        if (!Array.isArray(state[storeName][fieldName])) {
          state[storeName][fieldName] = [];
        }
        if (!Array.isArray(state[storeName][fieldName][payload.storeIndex])) {
          state[storeName][fieldName][payload.storeIndex] = [];
        }
        if (
          typeof state[storeName][fieldName][payload.storeIndex][
            payload.storeSecondIndex
          ] === "undefined"
        ) {
          state[storeName][fieldName][payload.storeIndex][
            payload.storeSecondIndex
          ] = {};
        }
        Vue.set(
          state[storeName][fieldName][payload.storeIndex],
          payload.storeSecondIndex,
          payload.data[fieldName]
        );
      } else {
        if (!Array.isArray(state[storeName][fieldName])) {
          state[storeName][fieldName] = [];
        }
        if (
          typeof state[storeName][fieldName][payload.storeIndex] === "undefined"
        ) {
          state[storeName][fieldName][payload.storeIndex] = {};
        }
        Vue.set(
          state[storeName][fieldName],
          payload.storeIndex,
          payload.data[fieldName]
        );
      }
    });
  },
};

export default {
  state,
  actions,
  mutations,
  getters,
};
